import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserRole } from '../../core/user-role.enum';
import { FileService } from '../../shared/components/orders/file-services/file.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { QueryParamsService } from '../../core/query-params.service';
import {
  BarcodePrimaryCodeOptions,
  BarcodePrimaryCode,
} from '../../shared/models/barcodes/barcode-primary-code.enum';
import {
  BarcodeIdCode,
  BarcodeIdCodeOptions,
  BarcodeMediumCode,
  BarcodeMediumCodeOptions,
  BarcodeWaterCode,
  BarcodeWaterCodeOptions,
} from '../../shared/models/barcodes/barcode-secondary-code.enum';
import { TableComponent } from '../../shared/components/barcodes/table/table.component';
import { BarcodeGenerationHistoryResponse } from '../../shared/models/barcodes/barcode-response.model';
import { BarcodeService } from '../../core/barcode.service';
import { LoggerService } from '../../core/logger.service';

@Component({
  selector: 'app-inspector',
  standalone: true,
  imports: [
    CommonModule,
    ProgressSpinnerModule,
    ButtonComponent,
    CalendarModule,
    DropdownModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    MultiSelectModule,
    TableComponent,
  ],
  templateUrl: './barcodes.component.html',
  styleUrl: './barcodes.component.scss',
})
export class BarcodesComponent implements OnInit {
  @ViewChild(TableComponent) tableComponent!: TableComponent;
  error: Error | null = null;

  barcodeHistoryList: BarcodeGenerationHistoryResponse[] = [];

  numberOfBarcodes: number | undefined;
  selectedPrimaryCode: BarcodePrimaryCode | undefined;
  selectedSecondaryCode: BarcodeIdCode | BarcodeMediumCode | BarcodeWaterCode | undefined;

  secondaryOptionsList:
    | { label: string; value: BarcodeIdCode | BarcodeMediumCode | BarcodeWaterCode }[]
    | [] = [];

  private readonly secondaryOptionsMap: Record<
    BarcodePrimaryCode,
    { label: string; value: BarcodeIdCode | BarcodeMediumCode | BarcodeWaterCode }[]
  > = {
    [BarcodePrimaryCode.MEDIUM_BARCODE]: BarcodeMediumCodeOptions,
    [BarcodePrimaryCode.ID_BARCODE]: BarcodeIdCodeOptions,
    [BarcodePrimaryCode.WATER_BARCODE]: BarcodeWaterCodeOptions,
  };

  private readonly defaultSecondaryByPrimary: Partial<
    Record<
      BarcodePrimaryCode,
      BarcodeIdCode | BarcodeMediumCode | BarcodeWaterCode
    >
  > = {
    [BarcodePrimaryCode.ID_BARCODE]: BarcodeIdCode.IDR_ID,
    [BarcodePrimaryCode.WATER_BARCODE]: BarcodeWaterCode.WATER,
  };

  primaryOptions = BarcodePrimaryCodeOptions;
  protected readonly UserRole = UserRole;

  constructor(
    private fileService: FileService,
    private queryService: QueryParamsService,
    private barcodeService: BarcodeService,
    private loggerService: LoggerService,
  ) {}

  get isDownloadDisabled(): boolean {
    const n = this.numberOfBarcodes;
    const validNumber =
      typeof n === 'number' && Number.isFinite(n) && n >= 1 && n <= 5000;
    return (
      !validNumber ||
      this.selectedPrimaryCode == null ||
      this.selectedSecondaryCode == null
    );
  }

  ngOnInit(): void {
    this.updateSecondaryOptions();
    this.barcodeService.getAllBarcodes().subscribe({
      next: (data) => {
        this.barcodeHistoryList = data;
      },
      error: (error: Error) => {
        this.loggerService.error('Error fetching barcodes');
        this.error = error;
      },
    });
    console.log(this.barcodeHistoryList);
  }

  onPrimaryChange(): void {
    this.updateSecondaryOptions();
    this.selectedSecondaryCode = this.selectedPrimaryCode
      ? this.defaultSecondaryByPrimary[this.selectedPrimaryCode]
      : undefined;
  }

  downloadBarcodeFile(): void {
    if (this.isDownloadDisabled) {
      return;
    }
    const barcodeParams: Record<string, any> = this.createBarcodeObject();
    const httpParams = this.queryService.createHttpParams(barcodeParams);
    this.fileService
      .downloadBarcodeFile(httpParams)
      .subscribe({
        next: (response) => {
          this.handleFileDownload(response);
        },
      });
  }

  handleFileDownload(response: any): void {
    const contentDispositionHeader =
      response.headers.get('Content-Disposition') || '';
    const fileName = this.fileService.extractFilename(contentDispositionHeader);
    const blob = response.body;
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }

  onNumberChange(value: any): void {
    const num = Number(value);
    if (Number.isFinite(num)) {
      this.numberOfBarcodes = Math.trunc(num);
    } else {
      this.numberOfBarcodes = undefined;
    }
  }

  private updateSecondaryOptions(): void {
    this.secondaryOptionsList = this.selectedPrimaryCode
      ? this.secondaryOptionsMap[this.selectedPrimaryCode] ?? []
      : [];
  }

  private createBarcodeObject(): Record<string, any> {
    const barcodeParameter: Record<string, any> = {};
    if (this.numberOfBarcodes) {
      barcodeParameter['numberOfBarcodes'] = this.numberOfBarcodes;
    }
    if (this.selectedPrimaryCode) {
      barcodeParameter['primaryCode'] = this.selectedPrimaryCode;
    }

    if (this.selectedSecondaryCode) {
      barcodeParameter['secondaryCode'] = this.selectedSecondaryCode;
    }
    return barcodeParameter;
  }
}
